import { useQuery, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  fetchClientSearchResults,
  fetchClientsList,
  type ClientListFilters,
} from '../../data/clients';
import supabaseClient from '../../lib/supabaseClient';
import type { ClientListRow, ClientSummary } from './index';
import { runSupabaseQuery, type AppError } from '../../utils/appErrors';

export const clientSearchQueryKey = (
  term: string,
  filters?: ClientListFilters,
): QueryKey => ['clients', 'search', term.trim().toLowerCase(), filters?.limit ?? 20];

type UseClientSearchQueryOptions = {
  term: string;
  filters?: ClientListFilters;
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const getClientSearchQueryOptions = ({
  term,
  filters,
  supabase = supabaseClient,
  enabled = true,
}: UseClientSearchQueryOptions) => ({
  queryKey: clientSearchQueryKey(term, filters),
  enabled: enabled && Boolean(term.trim()),
  queryFn: () => runSupabaseQuery(
    () => fetchClientSearchResults(term, { supabase }),
    'Unable to search customers.',
    'clients.search',
  ),
});

export const useClientSearchQuery = (
  options: UseClientSearchQueryOptions,
): UseQueryResult<ClientSummary[], AppError> => useQuery(getClientSearchQueryOptions(options));

export const clientsListQueryKey = (filters?: ClientListFilters): QueryKey => [
  'clients',
  'list',
  filters?.search ?? '',
  filters?.offset ?? 0,
  filters?.pageSize ?? filters?.limit ?? 50,
  filters?.sortKey ?? 'name',
  filters?.sortDir ?? 'asc',
  filters?.typeFilter ?? 'all',
  filters?.locationFilter ?? 'all',
  filters?.recentOnly ?? false,
];

type UseClientsListOptions = {
  filters?: ClientListFilters;
  supabase?: SupabaseClient;
  enabled?: boolean;
};

export const getClientsListQueryOptions = ({
  filters,
  supabase = supabaseClient,
  enabled = true,
}: UseClientsListOptions) => ({
  queryKey: clientsListQueryKey(filters),
  enabled,
  queryFn: () => runSupabaseQuery(async () => {
    const { data, count, error } = await fetchClientsList(filters, { supabase });
    return { data: { data, count }, error };
  }, 'Unable to load customers.', 'clients.list'),
  placeholderData: (
    previousData: { data: ClientListRow[]; count: number | null } | undefined,
  ) => previousData,
});

export const useClientsList = (
  options: UseClientsListOptions,
): UseQueryResult<{ data: ClientListRow[]; count: number | null }, AppError> =>
  useQuery(getClientsListQueryOptions(options));
